// shelbulaworkspaces/FileEditorPanel.jsx
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader, Save, Code, AlertTriangle, Search, Copy, Check, File, FileCode, FileJson, FileText, List, Braces, Plus, RefreshCw } from 'lucide-react';
import { apiManager } from './apiManager';
import { authManager } from '@/managers/AuthManager';
import UniversalEditor from '@/components/layout/RightPanel/UniversalEditor';
import { CONTENT_TYPES } from '@/managers/PanelManager';
import { Tree } from './FileTree';

/* ========================
 * buildFileTree (Local Helper) - Creates a nested tree structure from a flat file list
 * ========================*/
const buildFileTree = (files) => {
    const tree = {};
    // Sort files by path to improve traversal locality during tree construction.
    const sortedFiles = [...files].sort((a, b) => a.path.localeCompare(b.path));

    sortedFiles.forEach(file => {
        const pathParts = file.path.split('/');
        let currentLevel = tree;

        // Traverse or create directories for all parts except the last one
        for (let i = 0; i < pathParts.length - 1; i++) {
            const part = pathParts[i];
            if (!currentLevel[part]) {
                currentLevel[part] = {
                    name: part,
                    type: 'directory',
                    path: pathParts.slice(0, i + 1).join('/'),
                    children: {}
                };
            }
            // In case a file and a folder with same name exist in path, or path is weird
            if(currentLevel[part].type === 'directory'){
                currentLevel = currentLevel[part].children;
            }
        }

        // Handle the final part of the path (the actual file or an empty directory)
        const finalPart = pathParts[pathParts.length - 1];
        if (!currentLevel[finalPart]) {
            if (file.type === 'directory') {
                currentLevel[finalPart] = {
                    name: finalPart,
                    type: 'directory',
                    path: file.path,
                    children: {}
                };
            } else {
                // It's a file, so we add all metadata from the API response.
                currentLevel[finalPart] = {
                    ...file, // Keep all properties from the file object
                    name: finalPart,
                    children: null,
                };
            }
        } else {
            // If directory already exists, but now we see a file with metadata, update it
            if(file.type === 'file') {
                 currentLevel[finalPart] = { ...currentLevel[finalPart], ...file };
            }
        }
    });
    return tree;
};


/* ========================
 * getLanguageFromPath - Maps file extensions to editor language modes
 * ========================*/
const getLanguageFromPath = (filePath) => {
    if (!filePath) return 'plaintext';
    const extension = filePath.split('.').pop().toLowerCase();
    switch (extension) {
        case 'js': return 'javascript';
        case 'jsx': return 'jsx';
        case 'ts': return 'typescript';
        case 'tsx': return 'tsx';
        case 'css': return 'css';
        case 'html': return 'html';
        case 'json': return 'json';
        case 'md': return 'markdown';
        case 'py': return 'python';
        case 'java': return 'java';
        case 'sh': return 'shell';
        case 'yml':
        case 'yaml': return 'yaml';
        default: return 'plaintext';
    }
};

/* ========================
 * getFileIcon - Maps file extensions to lucide-react icons
 * ========================*/
const getFileIcon = (filePath) => {
    if (!filePath) return File;
    const extension = filePath.split('.').pop().toLowerCase();
    switch (extension) {
        case 'js':
        case 'ts':
        case 'jsx':
        case 'tsx':
        case 'py':
        case 'java':
        case 'html':
        case 'sh':
            return FileCode;
        case 'css':
             return Braces;
        case 'json':
            return FileJson;
        case 'md':
            return FileText;
        case 'yml':
        case 'yaml':
            return List;
        default:
            return File;
    }
};

/* ========================
 * Tab - Renders a single file tab in the editor header
 * ========================*/
const Tab = ({ file, isActive, onSelect, onClose }) => {
    const Icon = getFileIcon(file.path);
    return (
        <div
            className={`flex items-center justify-between pl-3 pr-1.5 border-r border-border-color cursor-pointer transition-colors whitespace-nowrap ${
                isActive ? 'bg-background-dark text-on-surface' : 'bg-background text-on-surface-dim hover:bg-surface'
            }`}
            onClick={() => onSelect(file.path)}
        >
            <div className="flex items-center min-w-0">
                <Icon className={`mr-2 flex-shrink-0 ${isActive ? 'text-primary-light' : 'text-on-surface-dim'}`} size={16} />
                <span className="text-sm font-medium mr-4 truncate">{file.name}</span>
            </div>
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    onClose(file.path);
                }}
                className="p-1 rounded-full text-on-surface-dim hover:bg-background-dark hover:text-on-surface flex-shrink-0"
            >
                <X size={16} />
            </button>
        </div>
    );
};

/* ========================
 * QuickOpenFileTree - A mini file explorer to quickly open unopened files
 * ========================*/
const QuickOpenFileTree = ({ allFiles, openFiles, onOpenFile, onClose }) => {
    const fileTree = useMemo(() => {
        const openFilePaths = new Set(openFiles.map(f => f.path));
        const availableFiles = allFiles.filter(f => !openFilePaths.has(f.path));
        return buildFileTree(availableFiles);
    }, [allFiles, openFiles]);

    const [expandedFolders, setExpandedFolders] = useState(new Set());

    const onToggleFolder = (path) => {
        setExpandedFolders(prev => {
            const newSet = new Set(prev);
            if (newSet.has(path)) {
                newSet.delete(path);
            } else {
                newSet.add(path);
            }
            return newSet;
        });
    };

    const handleOpenFile = (node) => {
        onOpenFile(node);
        onClose();
    };

    return (
        <div className="p-2 custom-scrollbar overflow-y-auto">
             {Object.keys(fileTree).length > 0 ? (
                <Tree 
                    nodes={fileTree} 
                    level={0} 
                    onOpenFile={handleOpenFile} 
                    expandedFolders={expandedFolders} 
                    onToggleFolder={onToggleFolder} 
                    showFileMetadata={false}
                />
            ) : (
                <div className="p-4 text-center text-xs text-on-surface-dim">
                    All workspace files are already open.
                </div>
            )}
        </div>
    )
}

/* ========================
 * ConfirmCloseDialog - A dialog for unsaved changes
 * ========================*/
const ConfirmCloseDialog = ({ onSave, onDontSave, onCancel, isSaving }) => (
     <div className="absolute inset-0 z-20 flex items-center justify-center bg-[rgba(var(--background-rgb),0.7)] backdrop-blur-sm">
        <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-surface border border-border-color rounded-lg shadow-xl p-6 w-full max-w-md"
        >
            <h2 className="text-lg font-bold text-on-surface flex items-center gap-2"><AlertTriangle size={20} className="text-yellow-500" />Unsaved Changes</h2>
            <p className="text-sm text-on-surface-dim mt-2">You have unsaved changes. Do you want to save them before closing?</p>
            <div className="mt-6 flex justify-end gap-3">
                <button onClick={onCancel} className="px-4 py-2 text-sm font-semibold rounded-md text-on-surface hover:bg-surface-dark transition-colors">
                    Cancel
                </button>
                <button onClick={onDontSave} className="px-4 py-2 text-sm font-semibold rounded-md bg-red-600/20 text-red-400 hover:bg-red-600/40 transition-colors">
                    Don't Save
                </button>
                <button onClick={onSave} disabled={isSaving} className="px-4 py-2 text-sm font-semibold rounded-md bg-primary text-white hover:bg-primary-dark transition-colors flex items-center gap-2 disabled:bg-primary/50">
                    {isSaving ? <Loader size={16} className="animate-spin" /> : <Save size={16} />}
                    Save & Close
                </button>
            </div>
        </motion.div>
    </div>
);


/* ========================
 * EditorToolbar - A small toolbar for editor actions like find and copy
 * ========================*/
const EditorToolbar = ({ onFind, onCopy, isCopied }) => (
    <div className="absolute top-3 right-3 z-10 flex items-center gap-1 bg-surface border border-border-color rounded-md shadow-lg p-1">
        <button onClick={onFind} className="p-1.5 rounded-md hover:bg-surface-dark text-on-surface-dim hover:text-on-surface transition-colors" title="Find (Ctrl+F)">
            <Search size={16} />
        </button>
        <div className="h-4 w-px bg-border-color" />
        <button onClick={onCopy} className="p-1.5 rounded-md hover:bg-surface-dark text-on-surface-dim hover:text-on-surface transition-colors" title="Copy Content">
            {isCopied ? <Check size={16} className="text-green-500" /> : <Copy size={16} />}
        </button>
    </div>
);


/* ========================
 * FileEditorPanel - A portal-based tabbed file editor panel
 * ========================*/
const FileEditorPanel = ({ openFiles, activeFile, onCloseFile, onSetActiveFile, onSave, onCloseAll, workspaceId, allFiles = [], onOpenFile, variant = 'modal', refreshToken = 0 }) => {
    const [contentCache, setContentCache] = useState({});
    const [initialContentCache, setInitialContentCache] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [authState, setAuthState] = useState(authManager.getState());
    const [showCloseConfirm, setShowCloseConfirm] = useState(false);
    const [isCopied, setIsCopied] = useState(false);
    const [isQuickOpenVisible, setQuickOpenVisible] = useState(false);
    const editorRef = useRef(null);
    const quickOpenRef = useRef(null);
    const quickOpenButtonRef = useRef(null);
    // Ref mirrors so loadFileContent can read both caches without being a dep of it,
    // preventing the cache-state → new callback → useEffect re-run infinite loop.
    const contentCacheRef = useRef(contentCache);
    const initialContentCacheRef = useRef(initialContentCache);
    useEffect(() => { contentCacheRef.current = contentCache; }, [contentCache]);
    useEffect(() => { initialContentCacheRef.current = initialContentCache; }, [initialContentCache]);

    const isOpen = openFiles.length > 0;
    const currentContent = activeFile ? contentCache[activeFile.path] || '' : '';
    const initialContent = activeFile ? initialContentCache[activeFile.path] || '' : '';
    const hasChanges = currentContent !== initialContent;

    useEffect(() => {
        const unsubscribe = authManager.subscribe(setAuthState);
        return () => unsubscribe();
    }, []);
    
    // Effect to handle clicks outside the quick open dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isQuickOpenVisible && quickOpenRef.current && !quickOpenRef.current.contains(event.target) && !quickOpenButtonRef.current.contains(event.target)) {
                setQuickOpenVisible(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isQuickOpenVisible]);


    const handleSave = useCallback(async () => {
        if (!activeFile || !workspaceId || !authState.user || isSaving || !hasChanges) return;
        setIsSaving(true);
        setError(null);
        try {
            const commitMessage = `[Human Commit] Updated ${activeFile.path}`;
            await onSave(activeFile.path, currentContent, commitMessage);
            setInitialContentCache(prev => ({ ...prev, [activeFile.path]: currentContent }));
        } catch (err) {
            console.error("Failed to save file:", err);
            setError(err.message || 'Could not save file.');
            throw err;
        } finally {
            setIsSaving(false);
        }
    }, [activeFile, workspaceId, authState.user, isSaving, hasChanges, onSave, currentContent]);

    const handleRequestClose = useCallback(() => {
        if (hasChanges) {
            setShowCloseConfirm(true);
        } else {
            onCloseAll();
        }
    }, [hasChanges, onCloseAll]);
    
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                if (isQuickOpenVisible) {
                    setQuickOpenVisible(false);
                } else {
                    handleRequestClose();
                }
            } else if ((event.ctrlKey || event.metaKey) && event.key === 's') {
                event.preventDefault();
                handleSave();
            }
        };

        if (isOpen) {
             window.addEventListener('keydown', handleKeyDown);
        }

        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleSave, handleRequestClose, isOpen, isQuickOpenVisible]);

    const loadFileContent = useCallback(async (filePath, { force = false } = {}) => {
        if (!filePath || !workspaceId || !authState.user) return;

        const hasCached = contentCacheRef.current[filePath] !== undefined;

        if (!hasCached || force) {
            // First open or explicit reload — show spinner.
            if (!hasCached) setIsLoading(true);
            setError(null);
            try {
                const data = await apiManager.readFileContent(workspaceId, filePath);
                const fileContent = typeof data?.content === 'string' ? data.content : '';
                setContentCache(prev => ({ ...prev, [filePath]: fileContent }));
                setInitialContentCache(prev => ({ ...prev, [filePath]: fileContent }));
            } catch (err) {
                console.error('Failed to read file:', err);
                setError(err.message || 'Could not load file content.');
                if (!hasCached) setContentCache(prev => ({ ...prev, [filePath]: '' }));
            } finally {
                if (!hasCached) setIsLoading(false);
            }
        } else {
            // Tab switch with cached content — silent background refresh.
            // Skip if the user has uncommitted edits so we don't clobber their work.
            const hasLocalEdits = contentCacheRef.current[filePath] !== initialContentCacheRef.current[filePath];
            if (hasLocalEdits) return;
            try {
                const data = await apiManager.readFileContent(workspaceId, filePath);
                const fileContent = typeof data?.content === 'string' ? data.content : '';
                setContentCache(prev => ({ ...prev, [filePath]: fileContent }));
                setInitialContentCache(prev => ({ ...prev, [filePath]: fileContent }));
            } catch { /* silent — stale cache is better than an error flash */ }
        }
    }, [authState.user, workspaceId]);

    useEffect(() => {
        if (!activeFile?.path) return;
        loadFileContent(activeFile.path);
    }, [activeFile?.path, loadFileContent]);

    useEffect(() => {
        if (!activeFile?.path || refreshToken === 0 || hasChanges) return;
        loadFileContent(activeFile.path, { force: true });
    }, [activeFile?.path, refreshToken, hasChanges, loadFileContent]);

    const handleReload = useCallback(async () => {
        if (!activeFile?.path || isSaving) return;
        if (hasChanges) {
            setError('Save or discard local edits before reloading this file from the workspace.');
            return;
        }

        await loadFileContent(activeFile.path, { force: true });
    }, [activeFile?.path, hasChanges, isSaving, loadFileContent]);
    
    const handleContentChange = useCallback((newText) => {
        if (activeFile) {
            setContentCache(prev => ({ ...prev, [activeFile.path]: newText }));
        }
    }, [activeFile]);

    const handleConfirmSaveAndClose = async () => {
        try {
            await handleSave();
            setShowCloseConfirm(false);
            onCloseAll();
        } catch (err) {
            console.error("Save failed, aborting close.");
        }
    };

    /* ========================
     * handleFind - Programmatically triggers the editor's find functionality.
     * ========================*/
    const handleFind = useCallback(() => {
        if (editorRef.current && typeof editorRef.current.find === 'function') {
            editorRef.current.find();
        } else {
            console.warn("Editor find action could not be triggered programmatically.");
        }
    }, []);

    const handleCopy = useCallback(() => {
        if (!activeFile || !currentContent) return;
        navigator.clipboard.writeText(currentContent).then(() => {
            setIsCopied(true);
            setTimeout(() => setIsCopied(false), 2000);
        });
    }, [activeFile, currentContent]);


    const isEmbedded = variant === 'embedded';

    const editorShell = (
        <motion.div
            initial={isEmbedded ? { opacity: 0 } : { opacity: 0, scale: 0.95 }}
            animate={isEmbedded ? { opacity: 1 } : { opacity: 1, scale: 1 }}
            exit={isEmbedded ? { opacity: 0 } : { opacity: 0, scale: 0.95 }}
            className={`relative bg-surface ${isEmbedded ? 'w-full h-full border-t border-border-color' : 'w-full max-w-6xl h-[90vh] rounded-lg border border-border-color shadow-2xl'} flex flex-col overflow-hidden`}
        >
                        {showCloseConfirm && (
                            <ConfirmCloseDialog 
                                onSave={handleConfirmSaveAndClose}
                                onDontSave={() => { setShowCloseConfirm(false); onCloseAll(); }}
                                onCancel={() => setShowCloseConfirm(false)}
                                isSaving={isSaving}
                            />
                        )}

                        {/* Header with Tabs and Save Controls */}
                        <div className="flex justify-between items-center border-b border-border-color flex-shrink-0 h-[45px] bg-background relative">
                            <div className="flex-1 flex items-stretch overflow-x-auto custom-scrollbar h-full border-r border-border-color">
                                {openFiles.map(file => (
                                    <Tab
                                        key={file.path}
                                        file={file}
                                        isActive={activeFile?.path === file.path}
                                        onSelect={onSetActiveFile}
                                        onClose={onCloseFile}
                                    />
                                ))}
                            </div>

                            <button
                                ref={quickOpenButtonRef}
                                onClick={() => setQuickOpenVisible(v => !v)}
                                className="p-2 h-full border-r border-l border-border-color text-on-surface-dim hover:text-on-surface hover:bg-surface transition-colors"
                                title="Open a file"
                            >
                                <Plus size={16} />
                            </button>
                            
                            <AnimatePresence>
                                {isQuickOpenVisible && (
                                    <motion.div
                                        ref={quickOpenRef}
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        className="absolute top-full left-0 mt-1 w-full max-w-xs max-h-96 z-20 bg-background border border-border-color rounded-md shadow-xl overflow-hidden"
                                    >
                                        <QuickOpenFileTree
                                            allFiles={allFiles}
                                            openFiles={openFiles}
                                            onOpenFile={onOpenFile}
                                            onClose={() => setQuickOpenVisible(false)}
                                        />
                                    </motion.div>
                                )}
                            </AnimatePresence>


                            <div className="flex items-center gap-3 p-2 pl-4 h-full">
                                <button
                                    onClick={handleReload}
                                    disabled={!activeFile || isLoading || isSaving}
                                    className="p-1.5 rounded-md text-on-surface-dim hover:text-on-surface hover:bg-surface-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    title="Reload file from workspace"
                                >
                                    <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
                                </button>
                                <AnimatePresence>
                                {hasChanges && activeFile && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -5 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -5 }}
                                        className="flex items-center gap-4"
                                    >
                                        <span className="text-xs text-on-surface-dim hidden md:inline">
                                            Press <kbd className="font-sans font-semibold">Cmd/Ctrl + S</kbd> to save
                                        </span>
                                        <button
                                            onClick={handleSave}
                                            disabled={isSaving}
                                            className="px-3 py-1 text-sm font-semibold rounded-md bg-primary text-white disabled:bg-primary/50 disabled:cursor-not-allowed hover:bg-primary-dark transition-colors flex items-center gap-2"
                                        >
                                            {isSaving ? <Loader size={16} className="animate-spin" /> : <Save size={16} />}
                                            Save
                                        </button>
                                    </motion.div>
                                )}
                                </AnimatePresence>
                                <button onClick={handleRequestClose} className="p-1 rounded-full text-on-surface-dim hover:bg-surface-dark hover:text-on-surface">
                                    <X size={20} />
                                </button>
                            </div>
                        </div>

                        {/* Editor Area */}
                        <div className="flex-1 overflow-auto relative bg-background-dark">
                             {error && <div className="absolute top-2 left-1/2 -translate-x-1/2 z-10 p-2 px-4 text-xs text-on-surface-dim opacity-60 bg-surface border border-border-color rounded-md shadow-sm">{error}</div>}
                            
                            {activeFile && !isLoading && (
                                <EditorToolbar onFind={handleFind} onCopy={handleCopy} isCopied={isCopied} />
                            )}

                            {activeFile ? (
                                isLoading ? (
                                    <div className="w-full h-full flex items-center justify-center text-on-surface-dim">
                                        <Loader size={24} className="animate-spin mr-2" /> Loading {activeFile.name}...
                                    </div>
                                ) : (
                                    <UniversalEditor
                                        ref={editorRef}
                                        contentString={currentContent}
                                        onContentChange={handleContentChange}
                                        language={getLanguageFromPath(activeFile.path)}
                                        contentType={CONTENT_TYPES.CODE}
                                        readOnly={isSaving}
                                        isDarkMode={true} 
                                        fontSize={14}
                                    />
                                )
                            ) : (
                                <div className="w-full h-full flex flex-col items-center justify-center text-on-surface-dim bg-background-dark">
                                    <Code size={48} className="mb-4 opacity-20" />
                                    <h3 className="text-lg font-semibold">No file selected</h3>
                                    <p className="text-sm">Select a file from the explorer to begin editing.</p>
                                </div>
                            )}
                        </div>
        </motion.div>
    );

    if (isEmbedded) {
        return (
            <AnimatePresence>
                {isOpen ? editorShell : null}
            </AnimatePresence>
        );
    }

    return ReactDOM.createPortal(
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-[1001] flex items-center justify-center p-4 bg-[rgba(var(--background-rgb),0.6)] backdrop-blur-sm">
                    {editorShell}
                </div>
            )}
        </AnimatePresence>,
        document.body
    );
};

export default FileEditorPanel;